import { Injectable } from '@angular/core';
import Bowser from 'bowser';

/**
 * localStorage key for the per-browser-install device identifier.
 *
 * Kept here (not in `TokenStorageService`) because the device id is a
 * non-secret, long-lived identifier used to scope server-side sessions to
 * a physical browser install — losing it does not compromise the user,
 * and we explicitly want it to survive `sessionStorage` clears that the
 * token migration in `TokenStorageService` performs.
 */
const DEVICE_ID_KEY = 'auth.deviceId';

/** Header names the backend expects on auth-initiating requests. */
export const DEVICE_ID_HEADER = 'x-device-id';
export const CLIENT_OS_HEADER = 'x-client-os';
export const CLIENT_BROWSER_HEADER = 'x-client-browser';
export const CLIENT_DEVICE_NAME_HEADER = 'x-client-device-name';

interface ParsedClientInfo {
  os: string;
  browser: string;
  deviceName: string;
}

/**
 * Issues and remembers a stable per-browser device id, and exposes the
 * `x-client-*` / `x-device-id` headers that auth-initiating endpoints
 * (`/auth/v1/login`, `/auth/v1/signup`) attach so the backend can fingerprint
 * the originating device.
 *
 * The id lives in `localStorage` (not `sessionStorage`) so it survives tab
 * reloads, sign-outs, and the token-storage migration; it is regenerated
 * only if the user clears site data or switches browsers.
 *
 * `User-Agent` is deliberately NOT in the header set: per WHATWG it is a
 * forbidden request header, so browsers silently drop any value JS tries
 * to set. The backend already receives the authoritative UA via the
 * native `User-Agent` header that the browser sends automatically.
 *
 * NOTE: every header in {@link getAuthRequestHeaders} is non-CORS-safelisted
 * and therefore triggers a preflight `OPTIONS` request. The backend's
 * `Access-Control-Allow-Headers` for the consuming endpoints must include
 * all of them or the browser will block the actual request.
 */
@Injectable({
  providedIn: 'root',
})
export class DeviceInfoService {
  private cachedDeviceId: string | null = null;
  private cachedClientInfo: ParsedClientInfo | null = null;

  /**
   * Returns the persisted device id, generating and storing one on first
   * call. Safe to invoke from route hooks; treat as idempotent.
   */
  ensureDeviceId(): string {
    // Storage first, so tabs converge on the id the browser holds (C-5);
    // then this tab's own memory, so a browser that cannot store or read the
    // id keeps one identity instead of minting one per request (F-027).
    const existing = this.readDeviceId();
    if (existing) {
      this.cachedDeviceId = existing;
      return existing;
    }
    if (this.cachedDeviceId) {
      return this.cachedDeviceId;
    }
    const generated = this.generateDeviceId();
    this.writeDeviceId(generated);
    this.cachedDeviceId = generated;
    return generated;
  }

  /**
   * Returns the stored device id without generating one. Returns `null`
   * if the user has never visited `/auth` on this browser install.
   */
  getDeviceId(): string | null {
    if (this.cachedDeviceId) {
      return this.cachedDeviceId;
    }
    const existing = this.readDeviceId();
    this.cachedDeviceId = existing;
    return existing;
  }

  /**
   * Headers to attach to login/signup HTTP requests so the backend can
   * record which device initiated the session.
   */
  getAuthRequestHeaders(): Record<string, string> {
    const deviceId = this.ensureDeviceId();
    const info = this.getClientInfo();
    return {
      [DEVICE_ID_HEADER]: deviceId,
      [CLIENT_OS_HEADER]: info.os,
      [CLIENT_BROWSER_HEADER]: info.browser,
      [CLIENT_DEVICE_NAME_HEADER]: info.deviceName,
    };
  }

  /**
   * The same OS / browser / device-name labels as the `x-client-*` headers,
   * for the realtime IDENTIFY frame (the owner's online list shows them).
   */
  getClientLabels(): { deviceName: string; os: string; browser: string } {
    const { deviceName, os, browser } = this.getClientInfo();
    return { deviceName, os, browser };
  }

  private getClientInfo(): ParsedClientInfo {
    if (this.cachedClientInfo) {
      return this.cachedClientInfo;
    }
    const userAgent = this.readUserAgent();
    const parsed = this.parseUserAgent(userAgent);
    this.cachedClientInfo = parsed;
    return parsed;
  }

  private parseUserAgent(userAgent: string): ParsedClientInfo {
    const fallback: ParsedClientInfo = {
      os: 'unknown',
      browser: 'unknown',
      deviceName: 'unknown',
    };
    if (!userAgent) {
      return fallback;
    }
    try {
      const result = Bowser.parse(userAgent);
      const os = this.composeName(result.os?.name, result.os?.version);
      const browser = this.composeName(
        result.browser?.name,
        result.browser?.version
      );
      const deviceName = this.composeDeviceName(result);
      return {
        os: os || fallback.os,
        browser: browser || fallback.browser,
        deviceName: deviceName || fallback.deviceName,
      };
    } catch {
      return fallback;
    }
  }

  private composeName(name?: string, version?: string): string {
    const trimmedName = name?.trim();
    const trimmedVersion = version?.trim();
    if (trimmedName && trimmedVersion) {
      return `${trimmedName} ${trimmedVersion}`;
    }
    return trimmedName ?? '';
  }

  private composeDeviceName(result: Bowser.Parser.ParsedResult): string {
    const vendor = result.platform?.vendor?.trim();
    const model = result.platform?.model?.trim();
    const type = result.platform?.type?.trim();
    const parts = [vendor, model, type].filter(
      (part): part is string => !!part
    );
    return parts.join(' ');
  }

  private readUserAgent(): string {
    if (typeof navigator === 'undefined') {
      return '';
    }
    return navigator.userAgent ?? '';
  }

  private generateDeviceId(): string {
    if (
      typeof crypto !== 'undefined' &&
      typeof crypto.randomUUID === 'function'
    ) {
      return crypto.randomUUID();
    }
    // Fallback for older environments: RFC4122 v4 from crypto.getRandomValues.
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0'));
      return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex
        .slice(6, 8)
        .join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
    }
    // Last-resort non-crypto fallback. Acceptable for a device identifier
    // (not a secret), but the modern paths above should always be taken.
    return `dev-${Date.now().toString(36)}-${Math.random()
      .toString(36)
      .slice(2, 10)}`;
  }

  private readDeviceId(): string | null {
    try {
      return localStorage.getItem(DEVICE_ID_KEY);
    } catch {
      return null;
    }
  }

  private writeDeviceId(value: string): void {
    try {
      localStorage.setItem(DEVICE_ID_KEY, value);
    } catch {
      // localStorage may be unavailable (private mode, quota); the id
      // still lives in `cachedDeviceId` for this tab's lifetime.
    }
  }
}
